import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import useUserContext from "../components/Authorization/useUserContext";
import { CustomTab } from "../models/customTabs";
import { buildHeaders } from "./utils";

const API_URI = import.meta.env.VITE_APP_API_URI;

// Cache custom tabs for 5 minutes - they rarely change during a session
const FIVE_MINUTES = 5 * 60 * 1000;

export const useCustomTabs = (enabled: boolean = true) => {
  const queryClient = useQueryClient();
  const user = useUserContext();
  const selectedRole = user?.selectedRoles?.[0];
  const queryKey = ["customTabs", selectedRole];
  const fetchCustomTabs = async (): Promise<CustomTab[]> => {
    const response = await axios.get<{ tabs: CustomTab[] }>(`${API_URI}/custom-tabs`, {
      headers: buildHeaders(user),
    });
    const tabs = response?.data?.tabs ?? [];
    // Trim whitespace from tab names to prevent display issues
    return tabs.map((tab) => ({
      ...tab,
      name: tab.name?.trim() ?? "",
    }));
  };

  const updateCustomTabs = async (tabs: CustomTab[]) => {
    // Trim tab names before saving to prevent whitespace issues
    const cleanedTabs = tabs.map((tab) => ({
      ...tab,
      name: tab.name?.trim() ?? "",
    }));

    const payload = {
      tabs: cleanedTabs,
    };

    return await axios.post(`${API_URI}/custom-tabs`, payload, {
      headers: buildHeaders(user),
    });
  };

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: fetchCustomTabs,
    staleTime: FIVE_MINUTES,
    cacheTime: FIVE_MINUTES,
    enabled,
  });

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomTabs,
    onMutate: async (newState: CustomTab[]) => {
      await queryClient.cancelQueries({ queryKey });
      const previousTabs = queryClient.getQueryData(queryKey);
      // Trim tab names in optimistic update as well
      const cleanedState = newState.map((tab) => ({
        ...tab,
        name: tab.name?.trim() ?? "",
      }));
      queryClient.setQueryData(queryKey, cleanedState);
      return { previousTabs };
    },
    onError: (_err, _newTabs, context) => {
      if (context?.previousTabs) {
        queryClient.setQueryData(queryKey, context.previousTabs);
      }
    },
  });
  return { customTabs: data ?? [], setCustomTabs: mutateAsync, isLoading };
};
